import React,
{
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
}
from 'react';
import masterVolume from '../masterVolume';
import
{
    createPreloadedAudio
}
from '../audioPreload';
import
{
    ACHIEVEMENTS,
    updateAchievementsWithGoobers
}
from './achievementsList';
import './achievements.css';

const STORAGE_KEY = 'aw24_achievements_unlocked_v1';
const DISCOVERED_CLASSES_KEY = 'aw24_discovered_classes_v1';
// Always use the same unlock sound for every achievement.
// Requested filename is "AchievmentSpecial.ogg" (typo), but the project also contains "AchievementSpecial.ogg".
const DEFAULT_UNLOCK_SOUND_CANDIDATES = ['/Audio/AchievmentSpecial.ogg', '/Audio/AchievementSpecial.ogg'];

const AchievementsContext = createContext(null);

function safeParseJson(str, fallback)
{
    try
    {
        const v = JSON.parse(str);
        return v ?? fallback;
    }
    catch (e)
    {
        return fallback;
    }
}

function normalizeUnlocked(value)
{
    if (!value) return [];
    if (Array.isArray(value)) return value.filter((x) => typeof x === 'string');
    if (value && typeof value === 'object' && Array.isArray(value.unlocked))
    {
        return value.unlocked.filter((x) => typeof x === 'string');
    }
    return [];
}

function getUnlockSoundCandidates(achievement)
{
    try
    {
        const override = achievement && Array.isArray(achievement.unlockSoundCandidates) ?
            achievement.unlockSoundCandidates.filter((s) => typeof s === 'string' && s) :
            [];
        if (override.length) return override;
    }
    catch (e)
    {}
    return DEFAULT_UNLOCK_SOUND_CANDIDATES;
}

async function tryPlayAudioCandidates(audioEls, candidates)
{
    for (const src of candidates || [])
    {
        const a = audioEls.get(src);
        if (!a) continue;
        // Always force achievement SFX to be scaled purely by master volume.
        try
        {
            a._baseVolume = 1.0;
        }
        catch (e)
        {}
        try
        {
            masterVolume.applyToElement(a);
        }
        catch (e)
        {}
        try
        {
            a.muted = false;
        }
        catch (e)
        {}
        try
        {
            a.currentTime = 0;
        }
        catch (e)
        {}
        try
        {
            await a.play();
            return true;
        }
        catch (e)
        {
            // keep trying other candidates
        }
    }
    return false;
}

export function AchievementsProvider(
{
    children
})
{
    const [unlocked, setUnlocked] = useState(() =>
    {
        try
        {
            const raw = localStorage.getItem(STORAGE_KEY);
            return normalizeUnlocked(safeParseJson(raw, []));
        }
        catch (e)
        {
            return [];
        }
    });

    const [resetNoticeVisible, setResetNoticeVisible] = useState(false);
    const resetNoticeTimerRef = useRef(null);

    const [unlockAnimKey, setUnlockAnimKey] = useState(0);
    const [unlockAnimVisible, setUnlockAnimVisible] = useState(false);
    const unlockAnimTimerRef = useRef(null);
    const [unlockAnimAchievement, setUnlockAnimAchievement] = useState(null);
    const boopDelayTimerRef = useRef(null);

    const unlockedSet = useMemo(() => new Set(unlocked), [unlocked]);

    const audioElsRef = useRef(new Map()); // src -> HTMLAudioElement
    const initedRef = useRef(false);

    const persistUnlocked = useCallback((nextUnlocked) =>
    {
        try
        {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUnlocked));
        }
        catch (e)
        {}
    }, []);

    const resetAll = useCallback(() =>
    {
        try
        {
            setUnlocked([]);
            try
            {
                localStorage.removeItem(STORAGE_KEY);
            }
            catch (e)
            {}
            try
            {
                localStorage.removeItem(DISCOVERED_CLASSES_KEY);
            }
            catch (e)
            {}
            try
            {
                window.dispatchEvent(new CustomEvent('achievements-reset'));
            }
            catch (e)
            {}
        }
        catch (e)
        {}

        try
        {
            setResetNoticeVisible(true);
            if (resetNoticeTimerRef.current)
            {
                clearTimeout(resetNoticeTimerRef.current);
                resetNoticeTimerRef.current = null;
            }
            resetNoticeTimerRef.current = setTimeout(() =>
            {
                setResetNoticeVisible(false);
                resetNoticeTimerRef.current = null;
            }, 2000);
        }
        catch (e)
        {}

        try
        {
            if (boopDelayTimerRef.current)
            {
                clearTimeout(boopDelayTimerRef.current);
                boopDelayTimerRef.current = null;
            }
        }
        catch (e)
        {}
    }, []);

    const preload = useCallback(() =>
    {
        if (initedRef.current) return;
        initedRef.current = true;

        // Create+load audio elements for any achievement sounds up front.
        const allCandidates = new Set();
        for (const a of ACHIEVEMENTS)
        {
            for (const src of a.soundCandidates || []) allCandidates.add(src);
            for (const src of a.unlockSoundCandidates || []) allCandidates.add(src);
        }
        for (const src of DEFAULT_UNLOCK_SOUND_CANDIDATES) allCandidates.add(src);

        for (const src of allCandidates)
        {
            try
            {
                const el = createPreloadedAudio(src,
                {
                    baseVolume: 1,
                    loop: false,
                    applyMaster: true,
                    muted: false,
                });
                if (!el) continue;
                audioElsRef.current.set(src, el);
            }
            catch (e)
            {
                // ignore
            }
        }

        // Preload achievement animation images (used by unlock popup).
        try
        {
            const flashy = new Image();
            flashy.src = '/Secrets Icons/flashy.png';
            for (const a of ACHIEVEMENTS)
            {
                const iconSrc = a && a.iconSrc ? String(a.iconSrc) : '';
                if (!iconSrc) continue;
                const icon = new Image();
                icon.src = iconSrc;
            }
        }
        catch (e)
        {}
    }, []);

    const isUnlocked = useCallback((id) => unlockedSet.has(id), [unlockedSet]);

    const unlock = useCallback(async (id) =>
    {
        const achievement = ACHIEVEMENTS.find((a) => a.id === id);
        if (!achievement) return false;
        if (unlockedSet.has(id)) return false;

        const next = [...unlockedSet, id];
        setUnlocked(next);
        persistUnlocked(next);

        // Play unlock sound (default is the Special sound; some achievements override).
        let usedCandidates = DEFAULT_UNLOCK_SOUND_CANDIDATES;
        try
        {
            const candidates = getUnlockSoundCandidates(achievement);
            usedCandidates = candidates;
            await tryPlayAudioCandidates(audioElsRef.current, candidates);
        }
        catch (e)
        {}

        // Show unlock animation (4 seconds).
        try
        {
            const isGeneric = (() =>
            {
                try
                {
                    // Treat as generic if it uses the generic sound override.
                    if (Array.isArray(achievement.unlockSoundCandidates) && achievement.unlockSoundCandidates.some((s) => String(s).includes('AchievementGeneric.ogg'))) return true;
                    if (Array.isArray(usedCandidates) && usedCandidates.some((s) => String(s).includes('AchievementGeneric.ogg'))) return true;
                }
                catch (e)
                {}
                return false;
            })();
            setUnlockAnimAchievement(
            {
                id,
                name: achievement.name || 'Secret Found',
                description: achievement.description || '',
                iconSrc: achievement.iconSrc || '/Secrets Icons/SecretTest.png',
                showFlashy: !isGeneric,
            });
            setUnlockAnimKey((k) => k + 1);
            setUnlockAnimVisible(true);
            if (unlockAnimTimerRef.current)
            {
                clearTimeout(unlockAnimTimerRef.current);
                unlockAnimTimerRef.current = null;
            }
            unlockAnimTimerRef.current = setTimeout(() =>
            {
                setUnlockAnimVisible(false);
                setUnlockAnimAchievement(null);
                unlockAnimTimerRef.current = null;
            }, 4000);
        }
        catch (e)
        {}

        // Broadcast so any page can react without wiring props.
        try
        {
            window.dispatchEvent(new CustomEvent('achievement-unlocked',
            {
                detail:
                {
                    id
                }
            }));
        }
        catch (e)
        {}

        return true;
    }, [persistUnlocked, unlockedSet]);

    useEffect(() =>
    {
        return () =>
        {
            if (unlockAnimTimerRef.current)
            {
                clearTimeout(unlockAnimTimerRef.current);
                unlockAnimTimerRef.current = null;
            }
            if (resetNoticeTimerRef.current)
            {
                clearTimeout(resetNoticeTimerRef.current);
                resetNoticeTimerRef.current = null;
            }
            if (boopDelayTimerRef.current)
            {
                clearTimeout(boopDelayTimerRef.current);
                boopDelayTimerRef.current = null;
            }
        };
    }, []);

    // Always preload as early as possible (App mounts during intro overlay).
    useEffect(() =>
    {
        preload();
    }, [preload]);

    // Load goobers data and update achievements list with class discoveries.
    useEffect(() =>
    {
        let cancelled = false;
        const loadGoobers = async () =>
        {
            try
            {
                const res = await fetch('/siteGoobers.json');
                const goobersList = await res.json();
                if (!cancelled && Array.isArray(goobersList))
                {
                    updateAchievementsWithGoobers(goobersList);
                    // Reset preload flag so audio/images get reloaded with new achievements.
                    initedRef.current = false;
                    // Preload any new achievement sounds.
                    try
                    {
                        preload();
                    }
                    catch (e)
                    {}
                }
            }
            catch (e)
            {
                // Goobers failed to load, but we can still run with static achievements.
            }
        };
        loadGoobers();
        return () =>
        {
            cancelled = true;
        };
    }, [preload]);

    // Listen for class discoveries and award achievements.
    useEffect(() =>
    {
        let discoveredClasses = [];
        try
        {
            const raw = localStorage.getItem(DISCOVERED_CLASSES_KEY);
            discoveredClasses = Array.isArray(safeParseJson(raw, [])) ? safeParseJson(raw, []) : [];
        }
        catch (e)
        {
            discoveredClasses = [];
        }

        const onClassSelected = (event) =>
        {
            try
            {
                const detail = event && event.detail;
                const className = detail && detail.name ? String(detail.name) : null;
                if (!className) return;

                // Check if this is a new discovery (skip Ari, the default).
                if (className.toLowerCase() === 'ari') return;
                if (discoveredClasses.includes(className)) return;

                // New class discovered!
                const nextDiscovered = [...discoveredClasses, className];
                try
                {
                    localStorage.setItem(DISCOVERED_CLASSES_KEY, JSON.stringify(nextDiscovered));
                }
                catch (e)
                {}
                discoveredClasses = nextDiscovered;

                // Award the discovery achievement.
                const achievementId = `discover-${className.toLowerCase()}`;
                try
                {
                    unlock(achievementId);
                }
                catch (e)
                {}
            }
            catch (e)
            {}
        };

        window.addEventListener('main-class-selected', onClassSelected);
        return () => window.removeEventListener('main-class-selected', onClassSelected);
    }, [unlock]);

    // Event-driven achievements (e.g. clicking Ari's nose hitbox).
    useEffect(() =>
    {
        const onBoop = () =>
        {
            try
            {
                if (boopDelayTimerRef.current) return;
                boopDelayTimerRef.current = setTimeout(() =>
                {
                    boopDelayTimerRef.current = null;
                    try
                    {
                        unlock('boop-ari-nose');
                    }
                    catch (e)
                    {}
                }, 700);
            }
            catch (e)
            {}
        };
        window.addEventListener('ari-nose-booped', onBoop);
        return () => window.removeEventListener('ari-nose-booped', onBoop);
    }, [unlock]);

    // Generic event-driven achievements.
    useEffect(() =>
    {
        const eventAchievements = ACHIEVEMENTS.filter((achievement) =>
        {
            const earnedBy = achievement && achievement.earnedBy;
            return earnedBy && earnedBy.type === 'event' && earnedBy.eventName && earnedBy.eventName !== 'ari-nose-booped';
        });

        const timerIds = [];

        const cleanups = eventAchievements.map((achievement) =>
        {
            const eventName = achievement.earnedBy.eventName;
            const delayMs = Number.isFinite(Number(achievement.earnedBy.delayMs)) ?
                Math.max(0, Number(achievement.earnedBy.delayMs)) :
                0;
            const onEvent = () =>
            {
                if (delayMs <= 0)
                {
                    try
                    {
                        unlock(achievement.id);
                    }
                    catch (e)
                    {}
                    return;
                }

                const timerId = setTimeout(() =>
                {
                    try
                    {
                        unlock(achievement.id);
                    }
                    catch (e)
                    {}
                }, delayMs);
                timerIds.push(timerId);
            };

            window.addEventListener(eventName, onEvent);
            return () => window.removeEventListener(eventName, onEvent);
        });

        return () =>
        {
            timerIds.forEach((timerId) => clearTimeout(timerId));
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [unlock]);

    // Global listeners for achievement triggers.
    useEffect(() =>
    {
        const pressedCodes = new Set();
        const resetChordArmedRef = {
            current: true
        };

        const onKeyDown = (e) =>
        {
            try
            {
                if (!e) return;
                // still track repeats for chord detection, but don't spam unlock/reset
                const isRepeat = !!e.repeat;

                if (e.code) pressedCodes.add(e.code);

                // Don't trigger while typing in inputs.
                const target = e.target;
                const tag = target && target.tagName ? String(target.tagName).toLowerCase() : '';
                const isTypingTarget =
                    tag === 'input' ||
                    tag === 'textarea' ||
                    (target && target.isContentEditable);
                if (isTypingTarget) return;

                // Test reset bind: Left Shift + R
                // "same time" implemented via held-keys set.
                if (!isRepeat && resetChordArmedRef.current && pressedCodes.has('ShiftLeft') && pressedCodes.has('KeyR'))
                {
                    resetChordArmedRef.current = false;
                    resetAll();
                    return;
                }

                // For now: single test achievement.
                if (!isRepeat && (e.key === '9' || e.code === 'Digit9'))
                {
                    unlock('test-press-9');
                }
                if (!isRepeat && (e.key === '8' || e.code === 'Digit8'))
                {
                    unlock('test-generic-secret');
                }
            }
            catch (err)
            {}
        };

        const onKeyUp = (e) =>
        {
            try
            {
                if (e && e.code) pressedCodes.delete(e.code);
                // re-arm once either chord key is released
                if (!pressedCodes.has('ShiftLeft') || !pressedCodes.has('KeyR'))
                {
                    resetChordArmedRef.current = true;
                }
            }
            catch (err)
            {}
        };

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () =>
        {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [unlock, resetAll]);

    const api = useMemo(() => (
    {
        preload,
        unlocked,
        isUnlocked,
        unlock,
    }), [preload, unlocked, isUnlocked, unlock]);

    return ( <AchievementsContext.Provider value = {
            api
        } >
        {
            children
        }

        {
            unlockAnimVisible && ( <div className = "aw24-achievement-overlay"
                key = {
                    unlockAnimKey
                } >
                <div className = "aw24-achievement-stage"
                aria-hidden = "true" >
                {
                    (unlockAnimAchievement && unlockAnimAchievement.showFlashy) && ( <div className = "aw24-achievement-flashyWrap" >
                        <img className = "aw24-achievement-flashy"
                        src = "/Secrets Icons/flashy.png"
                        alt = ""
                        draggable = {
                            false
                        }
                        /> </div>
                    )
                } <img className = "aw24-achievement-secret"
                src = {
                    (unlockAnimAchievement && unlockAnimAchievement.iconSrc) || '/Secrets Icons/SecretTest.png'
                }
                alt = ""
                draggable = {
                    false
                }
                />

                <div className = "aw24-achievement-text" >
                <div className = "aw24-achievement-title" >
                {
                    (unlockAnimAchievement && unlockAnimAchievement.name) || 'Secret Found'
                } </div> <div className = "aw24-achievement-desc" >
                {
                    (unlockAnimAchievement && unlockAnimAchievement.description) || ''
                } </div> </div> </div> </div>
            )
        }

        {
            resetNoticeVisible && ( <div style = {
                    {
                        position: 'fixed',
                        left: '50%',
                        bottom: '14vh',
                        transform: 'translateX(-50%)',
                        zIndex: 50000,
                        pointerEvents: 'none',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 18,
                        textAlign: 'center',
                        textShadow: '0 2px 10px rgba(0,0,0,0.85)',
                    }
                } >
                All secrets reset!
                </div>
            )
        } </AchievementsContext.Provider>
    );
}

export function useAchievements()
{
    const ctx = useContext(AchievementsContext);
    if (!ctx)
    {
        // Keep this non-throwy so it doesn't hard-crash random pages.
        return {
            preload: () =>
            {},
            unlocked: [],
            isUnlocked: () => false,
            unlock: async () => false,
        };
    }
    return ctx;
}
